import M from 'materialize-css'
import $ from 'jquery'

import { useEvent, useState } from 'hooks'
import { cn } from 'utils'
import {
	getCrossTableEditInfoElement,
	getCrossTableQuestionsOverviewSelectors,
	getCrossTableValueElementOfMetadataItem,
	getCrossTableTopBoxAnswersOfMetadataItem,
	getCrossTableBottomBoxAnswersOfMetadataItem,
	getCrossTableDetractorsOfMetadataItem,
	getCrossTablePromotersOfMetadataItem,
} from './selector'
import { crossTable } from './constant'

const { crossTableQuestionsOverview } = crossTable

type State = {
	questionsOverviewCollapsible?: HTMLElement
	metadataEditIcons?: NodeListOf<HTMLInputElement>
	metadataCheckboxes?: NodeListOf<HTMLInputElement>
	editMode: boolean
	activeSelect?: M.FormSelect
	topBoxAnswersSelect?: M.FormSelect
	bottomBoxAnswersSelect?: M.FormSelect
	detractorsSelect?: M.FormSelect
	promotersSelect?: M.FormSelect
}

export const CrossTableQuestionsOverview = (() => {
	const { state, setState } = useState<State>()
	setState({ editMode: false })

	const initCollapsible = () => {
		const { questionsOverviewCollapsible } = state
		if (!questionsOverviewCollapsible) return

		M.Collapsible.init(questionsOverviewCollapsible)
	}

	const handleEditIconClick = () => {
		const { metadataEditIcons } = state
		if (!metadataEditIcons) {
			return
		}

		const { length } = metadataEditIcons
		for (let i = 0; i < length; i++) {
			const icon = metadataEditIcons[i]
			const click = useEvent(icon, 'click')
			click.register(() => {
				const metadataItem = icon.parentElement
				if (!metadataItem) {
					return
				}

				toggleEditMode(icon)
				const valueElement = getCrossTableValueElementOfMetadataItem(metadataItem)
				const componentType = valueElement.getAttribute(crossTableQuestionsOverview.typeAttribute)
				const editComponent = createEditComponent(valueElement, componentType)
				if (!editComponent) {
					return
				}

				valueElement.replaceWith(editComponent)
				if (editComponent.tagName === 'SELECT') {
					setState({ activeSelect: M.FormSelect.init(editComponent) })
				}
				editComponent.focus()

				const components = createEditableAddedComponents(metadataItem, valueElement, editComponent)
				icon.replaceWith(createSubmitButtons(icon, components))
			})
		}
	}

	const handleCheckboxesClick = () => {
		const { metadataCheckboxes } = state
		if (!metadataCheckboxes) {
			return
		}

		const { length } = metadataCheckboxes
		for (let i = 0; i < length; i++) {
			const checkbox = metadataCheckboxes[i]
			const click = useEvent(checkbox, 'click')
			click.register(() => {
				// @ts-ignore
				$.nette.ajax({
					method: 'POST',
					url: checkbox.getAttribute(crossTableQuestionsOverview.handleAttribute),
					data: { value: checkbox.checked ? 'true' : 'false' },
					success: (payload: any) => {
						if (payload.success) {
							M.toast({ html: 'Success', classes: 'background-true' })
						} else if (payload.errorMessage) {
							M.toast({ html: payload.errorMessage, classes: 'background-false' })
						}
					},
				})
			})
		}
	}

	const createEditableAddedComponents = (
		metadataItem: HTMLElement,
		valueElement: HTMLElement,
		editComponent: HTMLInputElement | HTMLSelectElement,
	): any => {
		if (metadataItem.classList.contains(crossTableQuestionsOverview.calculations.average)) {
			return {
				edit: { element: valueElement, component: editComponent },
			}
		}

		if (metadataItem.classList.contains(crossTableQuestionsOverview.calculations.topBox)) {
			const answersElement = getCrossTableTopBoxAnswersOfMetadataItem(metadataItem)
			const answersType = answersElement.getAttribute(crossTableQuestionsOverview.typeAttribute)
			const answersComponent = createEditComponent(answersElement, answersType)
			if (answersComponent) {
				answersElement.replaceWith(answersComponent)
				if (answersComponent.tagName === 'SELECT') {
					setState({ topBoxAnswersSelect: M.FormSelect.init(answersComponent) })
				}
				answersComponent.focus()
				return {
					edit: { element: valueElement, component: editComponent },
					answers: { element: answersElement, component: answersComponent },
				}
			}
		}

		if (metadataItem.classList.contains(crossTableQuestionsOverview.calculations.bottomBox)) {
			const answersElement = getCrossTableBottomBoxAnswersOfMetadataItem(metadataItem)
			const answersType = answersElement.getAttribute(crossTableQuestionsOverview.typeAttribute)
			const answersComponent = createEditComponent(answersElement, answersType)
			if (answersComponent) {
				answersElement.replaceWith(answersComponent)
				if (answersComponent.tagName === 'SELECT') {
					setState({ bottomBoxAnswersSelect: M.FormSelect.init(answersComponent) })
				}
				answersComponent.focus()
				return {
					edit: { element: valueElement, component: editComponent },
					answers: { element: answersElement, component: answersComponent },
				}
			}
		}

		if (metadataItem.classList.contains(crossTableQuestionsOverview.calculations.nps)) {
			const detractorsElement = getCrossTableDetractorsOfMetadataItem(metadataItem)
			const detractorsType = detractorsElement.getAttribute(crossTableQuestionsOverview.typeAttribute)
			const detractorsComponent = createEditComponent(detractorsElement, detractorsType)

			const promotersElement = getCrossTablePromotersOfMetadataItem(metadataItem)
			const promotersType = promotersElement.getAttribute(crossTableQuestionsOverview.typeAttribute)
			const promotersComponent = createEditComponent(promotersElement, promotersType)
			if (detractorsComponent && promotersComponent) {
				detractorsElement.replaceWith(detractorsComponent)
				promotersElement.replaceWith(promotersComponent)
				if (detractorsComponent.tagName === 'SELECT') {
					setState({ detractorsSelect: M.FormSelect.init(detractorsComponent) })
				}
				detractorsComponent.focus()
				if (promotersComponent.tagName === 'SELECT') {
					setState({ promotersSelect: M.FormSelect.init(promotersComponent) })
				}
				promotersComponent.focus()
				return {
					edit: { element: valueElement, component: editComponent },
					detractors: { element: detractorsElement, component: detractorsComponent },
					promoters: { element: promotersElement, component: promotersComponent },
				}
			}
		}

		return {}
	}

	const toggleEditMode = (icon: HTMLElement) => {
		setState({ editMode: !state.editMode })
		toggleIcons()
		toggleEditInfo(icon)
	}

	const toggleIcons = () => {
		const { metadataEditIcons } = state
		if (!metadataEditIcons) {
			return
		}

		const { length } = metadataEditIcons
		for (let i = 0; i < length; i++) {
			const icon = metadataEditIcons[i]
			if (state.editMode) {
				cn.addClass(icon, 'hidden')
			} else {
				cn.removeClass(icon, 'hidden')
			}
		}
	}

	const toggleEditInfo = (icon: HTMLElement) => {
		const collapsibleItem = icon.closest(crossTableQuestionsOverview.questionsOverviewCollapsibleItem) as HTMLElement
		if (!collapsibleItem) {
			return
		}

		const editInfoElement = getCrossTableEditInfoElement(collapsibleItem)
		if (!editInfoElement) {
			return
		}

		if (state.editMode) {
			cn.removeClass(editInfoElement, 'hidden')
		} else {
			cn.addClass(editInfoElement, 'hidden')
		}
	}

	const createSubmitButtons = (icon: HTMLElement, components: any): HTMLElement => {
		const wrapper = document.createElement('DIV') as HTMLDivElement
		wrapper.className = 'action-buttons'
		const confirmButton = createButton('fas fa-check', 'green')
		const confirmClick = useEvent(confirmButton, 'click')
		confirmClick.register(() => {
			let ajaxData
			const metadataItem = components.edit.component.closest(crossTableQuestionsOverview.metadataItem)
			const valueValues = getComponentSelectedValues(components.edit.component)
			if (
				metadataItem.classList.contains(crossTableQuestionsOverview.calculations.topBox) ||
				metadataItem.classList.contains(crossTableQuestionsOverview.calculations.bottomBox)
			) {
				const answersValues = getComponentSelectedValues(components.answers.component)
				ajaxData = { value: valueValues, answers: answersValues }
			} else if (metadataItem.classList.contains(crossTableQuestionsOverview.calculations.nps)) {
				const detractorsValues = getComponentSelectedValues(components.detractors.component)
				const promotersValues = getComponentSelectedValues(components.promoters.component)
				ajaxData = {
					value: Number(valueValues),
					detractors: detractorsValues,
					promoters: promotersValues,
				}
			} else {
				ajaxData = { value: valueValues }
			}

			// @ts-ignore
			$.nette.ajax({
				method: 'POST',
				url: icon.getAttribute(crossTableQuestionsOverview.handleAttribute),
				data: ajaxData,
				success: (payload: any) => {
					if (payload.success) {
						// eslint-disable-next-line guard-for-in,no-restricted-syntax
						for (const key in components) {
							const component = components[key]
							const valValues = getComponentSelectedValues(component.component)
							component.element.setAttribute(crossTableQuestionsOverview.valueAttribute, JSON.stringify(valValues))
							const values = component.element.getAttribute(crossTableQuestionsOverview.editValuesAttribute)
							component.element.innerHTML = ''
							// eslint-disable-next-line guard-for-in,no-restricted-syntax,no-shadow
							for (const key in valValues) {
								const value = valValues[key]
								if (component.element.innerHTML !== '') {
									component.element.innerHTML += ', '
								}
								component.element.innerHTML += JSON.parse(values)[Number(value)]
							}
						}
						handleSubmitButtonChange(components, wrapper, icon)
						M.toast({ html: 'Success', classes: 'background-true' })
					} else if (payload.errorMessage) {
						M.toast({ html: payload.errorMessage, classes: 'background-false' })
					}
				},
			})
		})
		wrapper.appendChild(confirmButton)
		const cancelButton = createButton('fas fa-times', 'red')
		wrapper.appendChild(cancelButton)
		const cancelClick = useEvent(cancelButton, 'click')
		cancelClick.register(() => {
			handleSubmitButtonChange(components, wrapper, icon)
		})
		return wrapper
	}

	const handleSubmitButtonChange = (components: any, wrapper: HTMLElement, icon: HTMLElement) => {
		if (state.activeSelect) {
			state.activeSelect.destroy()
			setState({ activeSelect: null })
		}
		if (state.topBoxAnswersSelect) {
			state.topBoxAnswersSelect.destroy()
			setState({ topBoxAnswersSelect: null })
		}
		if (state.bottomBoxAnswersSelect) {
			state.bottomBoxAnswersSelect.destroy()
			setState({ bottomBoxAnswersSelect: null })
		}
		if (state.detractorsSelect) {
			state.detractorsSelect.destroy()
			setState({ detractorsSelect: null })
		}
		if (state.promotersSelect) {
			state.promotersSelect.destroy()
			setState({ promotersSelect: null })
		}

		// eslint-disable-next-line guard-for-in,no-restricted-syntax
		for (const key in components) {
			const component = components[key]
			component.component.replaceWith(component.element)
		}

		wrapper.replaceWith(icon)
		toggleEditMode(icon)
	}

	const createButton = (iconClassName: string, buttonClassName: string): HTMLButtonElement => {
		const button = document.createElement('BUTTON') as HTMLButtonElement
		button.className = `btn ${buttonClassName}`
		const icon = document.createElement('I') as HTMLElement
		icon.className = iconClassName
		button.appendChild(icon)
		return button
	}

	const createEditComponent = (valueElement: HTMLElement, componentType: string | null): HTMLSelectElement | null => {
		const possibleValues = valueElement.getAttribute(crossTableQuestionsOverview.editValuesAttribute)
		const currentValue = valueElement.getAttribute(crossTableQuestionsOverview.valueAttribute)
		// eslint-disable-next-line no-case-declarations
		const label = valueElement.getAttribute(crossTableQuestionsOverview.labelAttribute)
		let editComponent
		// eslint-disable-next-line default-case
		switch (componentType) {
			case 'select':
				// eslint-disable-next-line no-case-declarations
				const multipleValue = valueElement.getAttribute(crossTableQuestionsOverview.multipleAttribute)
				editComponent = document.createElement('SELECT') as HTMLSelectElement
				editComponent.multiple = multipleValue !== null && multipleValue === 'true'
				if (multipleValue !== null && multipleValue === 'true') {
					const option = document.createElement('OPTION') as HTMLOptionElement
					option.value = ''
					option.innerText = `${label} ...`
					option.disabled = true
					editComponent.append(option)
				}
				// eslint-disable-next-line no-case-declarations
				const parsedPossibleValues = JSON.parse(possibleValues ?? '')
				// eslint-disable-next-line no-case-declarations
				const parsedCurrentValues = JSON.parse(currentValue ?? '')
				// eslint-disable-next-line guard-for-in,no-restricted-syntax
				for (const key in parsedPossibleValues) {
					const value = parsedPossibleValues[Number(key)]
					const option = document.createElement('OPTION') as HTMLOptionElement
					option.value = key
					option.innerText = value
					if (parsedCurrentValues) {
						option.selected = parsedCurrentValues.indexOf(key) !== -1
					}
					editComponent.append(option)
				}
				break

			default:
				return null
		}
		return editComponent
	}

	const getComponentSelectedValues = (component: any): any => {
		const result = []
		const options = component.getElementsByTagName('OPTION')
		// eslint-disable-next-line guard-for-in,no-restricted-syntax
		for (const key in options) {
			const option = options[key]
			if (option.selected) {
				result.push(option.value)
			}
		}
		return result
	}

	const handleInitSelectors = () => {
		setState({
			...getCrossTableQuestionsOverviewSelectors(),
		})
	}

	const init = () => {
		handleInitSelectors()
		initCollapsible()
		handleEditIconClick()
		handleCheckboxesClick()
	}

	return {
		init,
	}
})()
